import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { getUser, getPropertyConfig } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, Check, Trash2, RotateCcw, Printer, Edit, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function Receiving() {
  const [receivings, setReceivings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedReceiving, setSelectedReceiving] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const user = getUser();
  const propConfig = getPropertyConfig();
  const hasPO = propConfig?.has_po !== false;
  const printRef = useRef();

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const canCreate = userRoles.some(r => ['Admin', 'Receiving'].includes(r));

  // Filter receivings based on search query
  const filteredReceivings = receivings.filter(rcv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      rcv.receiving_number?.toLowerCase().includes(query) ||
      rcv.supplier_name?.toLowerCase().includes(query) ||
      rcv.location?.toLowerCase().includes(query) ||
      rcv.document?.toLowerCase().includes(query) ||
      rcv.status?.toLowerCase().includes(query)
    );
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recvRes, suppRes, wareRes] = await Promise.all([
        api.get('/receiving'),
        api.get('/suppliers'),
        api.get('/warehouses')
      ]);
      setReceivings(recvRes.data);
      setSuppliers(suppRes.data);
      setWarehouses(wareRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async (receivingNumber) => {
    if (!window.confirm('Release this receiving? Stock will be updated.')) return;
    
    try {
      await api.patch(`/receiving/${receivingNumber}/release`);
      toast.success('Receiving released successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release');
    }
  };

  const handleDelete = async (receivingNumber) => {
    if (!window.confirm('Are you sure you want to delete this Receiving?')) return;
    
    try {
      await api.delete(`/receiving/${receivingNumber}`);
      toast.success('Receiving deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleReopen = async (receivingNumber) => {
    if (!window.confirm('Reopen this Receiving? Status will be changed to Open and stock will be reversed.')) return;
    
    try {
      await api.patch(`/receiving/${receivingNumber}/reopen`);
      toast.success('Receiving reopened successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen');
    }
  };

  const handlePrint = (rcv) => {
    setSelectedReceiving(rcv);
    setPrintDialogOpen(true);
  };

  const handleEdit = (rcv) => {
    setSelectedReceiving(rcv);
    setEditDialogOpen(true);
  };

  const executePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Receiving - ${selectedReceiving?.receiving_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h1 { font-size: 18px; margin: 0; }
            .header h2 { font-size: 14px; margin: 5px 0; font-weight: normal; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #333; padding: 8px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .total-row { font-weight: bold; background-color: #f0f0f0; }
            .signature-section { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-top: 40px; text-align: center; }
            .signature-box { border: 1px solid #333; padding: 15px; min-height: 100px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="receiving-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">{hasPO ? 'Receiving' : 'Barang Masuk'}</h1>
          <p className="text-muted-foreground">{hasPO ? 'Manage goods receiving' : 'Catat barang masuk ke gudang'}</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-receiving-button">
                <Plus className="h-4 w-4 mr-2" />
                {hasPO ? 'Create Receiving' : 'Barang Masuk Baru'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{hasPO ? 'Create Receiving' : 'Barang Masuk Baru'}</DialogTitle>
              </DialogHeader>
              <ReceivingForm 
                suppliers={suppliers} 
                warehouses={warehouses}
                onSuccess={() => {
                  setDialogOpen(false);
                  fetchData();
                }} 
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by number, supplier, location, document..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-2 border-primary/30 focus:border-primary"
              data-testid="receiving-search-input"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-muted-foreground mt-2">
              Found {filteredReceivings.length} of {receivings.length} receiving documents
            </p>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="receiving-table">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-3 font-semibold">{hasPO ? 'Receiving Number' : 'No. Dokumen'}</th>
                <th className="text-left p-3 font-semibold">Date</th>
                {hasPO && <th className="text-left p-3 font-semibold">Supplier</th>}
                <th className="text-left p-3 font-semibold">Location</th>
                <th className="text-right p-3 font-semibold">{hasPO ? 'Amount' : 'Total Qty'}</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-center p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReceivings.map((rcv) => (
                <tr key={rcv.receiving_number} className="border-b hover:bg-accent/20 transition-colors">
                  <td className="p-3 font-medium">{rcv.receiving_number}</td>
                  <td className="p-3">{new Date(rcv.date).toLocaleDateString('id-ID')}</td>
                  {hasPO && <td className="p-3">{rcv.supplier_name}</td>}
                  <td className="p-3">{rcv.location}</td>
                  <td className="p-3 text-right tabular-nums font-medium">
                    {hasPO ? formatRupiah(rcv.total_amount) : rcv.items?.reduce((sum, i) => sum + i.quantity, 0)}
                  </td>
                  <td className="p-3">
                    <span className={`status-badge status-${rcv.status.toLowerCase()}`}>
                      {rcv.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedReceiving(rcv);
                          setViewDialogOpen(true);
                        }}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePrint(rcv)}
                        title="Print Receiving"
                      >
                        <Printer className="h-4 w-4 text-blue-600" />
                      </Button>
                      {rcv.status === 'Open' && canCreate && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(rcv)}
                            data-testid={`edit-receiving-${rcv.receiving_number}`}
                            title="Edit Receiving"
                          >
                            <Edit className="h-4 w-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRelease(rcv.receiving_number)}
                            data-testid={`release-receiving-${rcv.receiving_number}`}
                            title="Release Receiving"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(rcv.receiving_number)}
                            data-testid={`delete-receiving-${rcv.receiving_number}`}
                            title="Delete Receiving"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                      {rcv.status === 'Release' && userRoles.includes('Admin') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleReopen(rcv.receiving_number)}
                          data-testid={`reopen-receiving-${rcv.receiving_number}`}
                          title="Reopen Receiving (Admin Only)"
                        >
                          <RotateCcw className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Receiving Details</DialogTitle>
          </DialogHeader>
          {selectedReceiving && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Receiving Number</Label>
                  <p className="font-medium">{selectedReceiving.receiving_number}</p>
                </div>
                <div>
                  <Label>Date</Label>
                  <p className="font-medium">{new Date(selectedReceiving.date).toLocaleDateString('id-ID')}</p>
                </div>
                <div>
                  <Label>Document Reference</Label>
                  <p className="font-medium">{selectedReceiving.document || '-'}</p>
                </div>
                <div>
                  <Label>Supplier</Label>
                  <p className="font-medium">{selectedReceiving.supplier_name}</p>
                </div>
                <div>
                  <Label>Location</Label>
                  <p className="font-medium">{selectedReceiving.location}</p>
                </div>
                <div>
                  <Label>Created By</Label>
                  <p className="font-medium">{selectedReceiving.made_by || selectedReceiving.created_by}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <p className="font-medium">{selectedReceiving.status}</p>
                </div>
              </div>
              {selectedReceiving.remark && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <Label className="text-yellow-800">Remark</Label>
                  <p className="font-medium text-yellow-900">{selectedReceiving.remark}</p>
                </div>
              )}
              <div>
                <Label className="mb-2 block">Items</Label>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-secondary/50">
                      <th className="text-left p-2">PO Number</th>
                      <th className="text-left p-2">Item</th>
                      <th className="text-center p-2">Qty DO</th>
                      <th className="text-center p-2">Qty Fisik</th>
                      <th className="text-center p-2">Selisih</th>
                      <th className="text-left p-2">Alasan</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReceiving.items.map((item, idx) => {
                      const delivered = item.qty_delivered ?? item.quantity;
                      const variance = (item.quantity || 0) - (delivered || 0);
                      return (
                      <tr key={idx} className={`border-b ${variance !== 0 ? 'bg-orange-50/40' : ''}`}>
                        <td className="p-2">{item.po_number}</td>
                        <td className="p-2">{item.item_name}</td>
                        <td className="p-2 text-center">{delivered} {item.unit}</td>
                        <td className="p-2 text-center font-semibold">{item.quantity} {item.unit}</td>
                        <td className={`p-2 text-center font-semibold ${variance < 0 ? 'text-red-600' : variance > 0 ? 'text-green-600' : ''}`}>
                          {variance === 0 ? '-' : (variance > 0 ? `+${variance}` : variance)}
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">{item.variance_reason || '-'}</td>
                        <td className="p-2 text-right tabular-nums">
                          {formatRupiah(item.amount)}
                        </td>
                      </tr>
                    );})}
                  </tbody>
                  <tfoot>
                    <tr className="font-bold bg-secondary/30">
                      <td colSpan="6" className="p-2 text-right">Total:</td>
                      <td className="p-2 text-right tabular-nums">{formatRupiah(selectedReceiving.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="flex justify-end pt-4">
                <Button onClick={() => handlePrint(selectedReceiving)} className="btn-primary">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Receiving
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Receiving - {selectedReceiving?.receiving_number}</DialogTitle>
          </DialogHeader>
          {selectedReceiving && (
            <ReceivingEditForm 
              receiving={selectedReceiving}
              warehouses={warehouses}
              onSuccess={() => {
                setEditDialogOpen(false);
                setSelectedReceiving(null);
                fetchData();
              }} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Print Preview - {selectedReceiving?.receiving_number}</DialogTitle>
          </DialogHeader>
          {selectedReceiving && (
            <>
              <div ref={printRef} className="bg-white p-6 border rounded">
                {/* Header */}
                <div className="header text-center mb-6">
                  <h1 className="text-xl font-bold">ROYAL TULIP BATU</h1>
                  <h2 className="text-lg font-semibold mt-2">GOODS RECEIVING NOTE</h2>
                  <p className="text-sm text-gray-600">{selectedReceiving.receiving_number}</p>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="w-28 font-semibold">No. Receiving</span>
                      <span>: {selectedReceiving.receiving_number}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Tanggal</span>
                      <span>: {new Date(selectedReceiving.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Document</span>
                      <span>: {selectedReceiving.document}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="w-28 font-semibold">Supplier</span>
                      <span>: {selectedReceiving.supplier_name}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Lokasi</span>
                      <span>: {selectedReceiving.location}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Status</span>
                      <span>: {selectedReceiving.status}</span>
                    </div>
                  </div>
                </div>

                {/* Remark if exists */}
                {selectedReceiving.remark && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                    <span className="font-semibold text-yellow-800">Remark: </span>
                    <span className="text-yellow-900">{selectedReceiving.remark}</span>
                  </div>
                )}

                {/* Items Table */}
                <table className="w-full border-collapse mb-6">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 p-2 text-center w-12">No</th>
                      <th className="border border-gray-300 p-2 text-left">No. PO</th>
                      <th className="border border-gray-300 p-2 text-left">Barang</th>
                      <th className="border border-gray-300 p-2 text-center w-20">Qty</th>
                      <th className="border border-gray-300 p-2 text-center w-20">Satuan</th>
                      <th className="border border-gray-300 p-2 text-right w-36">Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReceiving.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="border border-gray-300 p-2 text-center">{idx + 1}</td>
                        <td className="border border-gray-300 p-2">{item.po_number}</td>
                        <td className="border border-gray-300 p-2">{item.item_name}</td>
                        <td className="border border-gray-300 p-2 text-center">{item.quantity}</td>
                        <td className="border border-gray-300 p-2 text-center">{item.unit}</td>
                        <td className="border border-gray-300 p-2 text-right">{formatRupiah(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan="5" className="border border-gray-300 p-2 text-right">TOTAL</td>
                      <td className="border border-gray-300 p-2 text-right">{formatRupiah(selectedReceiving.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>

                {/* Signature Section */}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="border border-gray-300 p-4 text-center">
                    <p className="font-semibold mb-16">Diterima Oleh</p>
                    <div className="border-t border-gray-400 pt-2 mt-2">
                      <p className="font-medium">(________________)</p>
                    </div>
                  </div>
                  <div className="border border-gray-300 p-4 text-center">
                    <p className="font-semibold mb-16">Diperiksa Oleh</p>
                    <div className="border-t border-gray-400 pt-2 mt-2">
                      <p className="font-medium">(________________)</p>
                    </div>
                  </div>
                  <div className="border border-gray-300 p-4 text-center">
                    <p className="font-semibold mb-16">Disetujui Oleh</p>
                    <div className="border-t border-gray-400 pt-2 mt-2">
                      <p className="font-medium">(________________)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
                  Close
                </Button>
                <Button onClick={executePrint} className="btn-primary">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReceivingForm({ suppliers, warehouses, onSuccess }) {
  const propConfig = getPropertyConfig();
  const hasPO = propConfig?.has_po !== false;
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    document: '',
    date: new Date().toISOString().split('T')[0],
    supplier_id: '',
    location: '',
    remark: '',
    items: []
  });
  const [availablePOs, setAvailablePOs] = useState([]);
  const [poRemarks, setPoRemarks] = useState({});

  useEffect(() => {
    if (!hasPO) {
      api.get('/items').then(res => setItems(res.data)).catch(() => {});
    }
  }, [hasPO]);

  useEffect(() => {
    const fetchPOs = async () => {
      try {
        const res = await api.get(`/purchase-orders/supplier/${formData.supplier_id}/released`);
        const poItems = [];
        const remarks = {};
        res.data.forEach(po => {
          if (po.remark) {
            remarks[po.po_number] = po.remark;
          }
          po.items.forEach(item => {
            poItems.push({
              po_number: po.po_number,
              po_remark: po.remark || '',
              item_id: item.item_id,
              item_name: item.item_name,
              unit: item.unit,
              price: item.price,
              available_qty: item.quantity,  // outstanding remaining from backend
              ordered_qty: item.ordered_qty || item.quantity,
              received_qty_before: item.received_qty || 0,
              qty_delivered: 0,   // qty on supplier delivery note
              quantity: 0,        // qty fisik (physical count) → goes to stock
              variance_reason: '',
              selected: false
            });
          });
        });
        setAvailablePOs(poItems);
        setPoRemarks(remarks);
      } catch (error) {
        toast.error('Failed to fetch POs');
      }
    };

    if (hasPO && formData.supplier_id) {
      fetchPOs();
    }
  }, [formData.supplier_id, hasPO]);

  const handleItemToggle = (index) => {
    const newItems = [...availablePOs];
    newItems[index].selected = !newItems[index].selected;
    if (!newItems[index].selected) {
      newItems[index].quantity = 0;
      newItems[index].qty_delivered = 0;
      newItems[index].variance_reason = '';
    }
    setAvailablePOs(newItems);
  };

  const handleDeliveredChange = (index, qty) => {
    const newItems = [...availablePOs];
    const v = parseFloat(qty) || 0;
    newItems[index].qty_delivered = v;
    // Auto-fill physical qty to delivered on first entry (user can adjust)
    if (!newItems[index].quantity || newItems[index].quantity === 0) {
      newItems[index].quantity = v;
    }
    setAvailablePOs(newItems);
  };

  const handleQuantityChange = (index, qty) => {
    const newItems = [...availablePOs];
    newItems[index].quantity = parseFloat(qty) || 0;
    setAvailablePOs(newItems);
  };

  const handleVarianceReasonChange = (index, val) => {
    const newItems = [...availablePOs];
    newItems[index].variance_reason = val;
    setAvailablePOs(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let selectedItems;
    if (hasPO) {
      // Validate: if delivered != received, variance_reason required
      const missing = availablePOs.find(it => it.selected && it.quantity > 0 && (it.qty_delivered || 0) !== it.quantity && !it.variance_reason);
      if (missing) {
        toast.error(`Isi alasan selisih untuk item: ${missing.item_name}`);
        return;
      }
      selectedItems = availablePOs
        .filter(item => item.selected && item.quantity > 0)
        .map(item => ({
          po_number: item.po_number,
          item_id: item.item_id,
          item_name: item.item_name,
          unit: item.unit,
          quantity: item.quantity,  // physical qty → stock
          qty_delivered: item.qty_delivered || item.quantity,
          variance_reason: item.variance_reason || null,
          amount: item.quantity * item.price
        }));
    } else {
      selectedItems = formData.items.filter(item => item.quantity > 0);
    }

    if (selectedItems.length === 0) {
      toast.error('Pilih item dan masukkan quantity');
      return;
    }

    try {
      const payload = {
        document: formData.document,
        date: new Date(formData.date).toISOString(),
        location: formData.location,
        remark: formData.remark,
        items: selectedItems
      };
      if (hasPO) payload.supplier_id = formData.supplier_id;
      
      await api.post('/receiving', payload);
      toast.success('Receiving berhasil dibuat');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create receiving');
    }
  };

  const addDirectItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_id: '', item_name: '', unit: '', quantity: 0, amount: 0 }]
    }));
  };

  const updateDirectItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        if (item) {
          newItems[index] = { ...newItems[index], item_id: value, item_name: item.name, unit: item.unit };
        }
      } else {
        newItems[index] = { ...newItems[index], [field]: field === 'quantity' ? (parseFloat(value) || 0) : value };
      }
      return { ...prev, items: newItems };
    });
  };

  const removeDirectItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Document Reference</Label>
          <Input
            value={formData.document}
            onChange={(e) => setFormData({ ...formData, document: e.target.value })}
            required
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
        <div>
          <Label>Date</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {hasPO && (
          <div>
            <Label>Supplier</Label>
            <Select value={formData.supplier_id} onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}>
              <SelectTrigger className="bg-white border-2 border-primary/30">
                <SelectValue placeholder="Select supplier" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Location (Warehouse)</Label>
          <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
            <SelectTrigger className="bg-white border-2 border-primary/30">
              <SelectValue placeholder="Select warehouse" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((wh) => (
                <SelectItem key={wh.id} value={wh.id}>
                  {wh.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Direct item entry for non-PO properties */}
      {!hasPO && (
        <div className="border-t pt-4">
          <div className="flex justify-between items-center mb-2">
            <Label>Items</Label>
            <Button type="button" variant="outline" size="sm" onClick={addDirectItem}>
              <Plus className="h-3 w-3 mr-1" /> Tambah Item
            </Button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {formData.items.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-end">
                <div className="flex-1">
                  {idx === 0 && <Label className="text-xs">Item</Label>}
                  <select
                    value={item.item_id}
                    onChange={(e) => updateDirectItem(idx, 'item_id', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Pilih item...</option>
                    {items.map(i => (
                      <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
                    ))}
                  </select>
                </div>
                <div className="w-24">
                  {idx === 0 && <Label className="text-xs">Qty</Label>}
                  <Input
                    type="number"
                    min="0"
                    value={item.quantity || ''}
                    onChange={(e) => updateDirectItem(idx, 'quantity', e.target.value)}
                    placeholder="Qty"
                  />
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeDirectItem(idx)} className="shrink-0">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            {formData.items.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Klik &quot;Tambah Item&quot; untuk menambahkan barang</p>
            )}
          </div>
        </div>
      )}

      {availablePOs.length > 0 && (
        <div className="border-t pt-4">
          <Label className="mb-2 block">Select Items from POs</Label>
          
          {/* Show PO Remarks if any */}
          {Object.keys(poRemarks).length > 0 && (
            <div className="mb-4 space-y-2">
              <p className="text-sm font-medium text-primary">PO Remarks:</p>
              {Object.entries(poRemarks).map(([poNum, remark]) => (
                <div key={poNum} className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <span className="font-semibold text-yellow-800">{poNum}:</span>
                  <span className="ml-2 text-yellow-900">{remark}</span>
                </div>
              ))}
            </div>
          )}
          
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b bg-secondary/50">
                  <th className="text-left p-2 w-10">Sel</th>
                  <th className="text-left p-2">PO</th>
                  <th className="text-left p-2">Item</th>
                  <th className="text-center p-2 w-20" title="Sisa qty yang belum diterima">Sisa</th>
                  <th className="text-center p-2 w-24" title="Qty di surat jalan/nota supplier">Qty DO</th>
                  <th className="text-center p-2 w-24" title="Qty fisik hasil hitung — yang masuk stok">Qty Fisik</th>
                  <th className="text-center p-2 w-20">Selisih</th>
                  <th className="text-left p-2 w-40">Alasan Selisih</th>
                </tr>
              </thead>
              <tbody>
                {availablePOs.map((item, idx) => {
                  const variance = (item.quantity || 0) - (item.qty_delivered || 0);
                  const hasVariance = item.selected && item.quantity > 0 && variance !== 0;
                  return (
                  <tr key={idx} className={`border-b ${item.po_remark ? 'bg-yellow-50/50' : ''} ${hasVariance ? 'bg-orange-50/60' : ''}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => handleItemToggle(idx)}
                        className="w-4 h-4"
                        data-testid={`rcv-select-${idx}`}
                      />
                    </td>
                    <td className="p-2 text-xs">
                      <span className="font-mono">{item.po_number}</span>
                      {item.po_remark && (
                        <span className="ml-1 text-yellow-600" title={item.po_remark}>📝</span>
                      )}
                    </td>
                    <td className="p-2">
                      <div>{item.item_name}</div>
                      {item.received_qty_before > 0 && (
                        <div className="text-[10px] text-muted-foreground">
                          Ordered: {item.ordered_qty} • Received: {item.received_qty_before}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-center tabular-nums font-medium text-blue-700">{item.available_qty}</td>
                    <td className="p-2">
                      <Input
                        type="number"
                        value={item.qty_delivered || ''}
                        onChange={(e) => handleDeliveredChange(idx, e.target.value)}
                        placeholder="DO"
                        disabled={!item.selected}
                        step="0.01"
                        className="w-20 h-8 bg-white border-2 border-primary/20 text-center"
                        data-testid={`rcv-delivered-${idx}`}
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        value={item.quantity || ''}
                        onChange={(e) => handleQuantityChange(idx, e.target.value)}
                        placeholder="Fisik"
                        disabled={!item.selected}
                        step="0.01"
                        className="w-20 h-8 bg-white border-2 border-green-400 text-center font-semibold"
                        data-testid={`rcv-physical-${idx}`}
                      />
                    </td>
                    <td className={`p-2 text-center tabular-nums font-semibold ${variance < 0 ? 'text-red-600' : variance > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {item.selected && item.qty_delivered > 0 ? (variance > 0 ? `+${variance}` : variance) : '-'}
                    </td>
                    <td className="p-2">
                      <Input
                        type="text"
                        value={item.variance_reason || ''}
                        onChange={(e) => handleVarianceReasonChange(idx, e.target.value)}
                        placeholder={hasVariance ? 'Wajib diisi' : 'Opsional'}
                        disabled={!item.selected || !hasVariance}
                        className={`h-8 text-xs bg-white border-2 ${hasVariance ? 'border-orange-400' : 'border-gray-200'}`}
                        data-testid={`rcv-variance-reason-${idx}`}
                      />
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            💡 <b>Qty DO</b> = qty di surat jalan supplier. <b>Qty Fisik</b> = hasil hitung petugas (yang masuk stok). Kalau berbeda, isi <b>Alasan Selisih</b>. Sisa qty yang belum diterima tetap bisa direceive di transaksi berikutnya.
          </p>
        </div>
      )}

      {/* Receiving Remark */}
      <div>
        <Label>Receiving Remark (Optional)</Label>
        <Textarea
          value={formData.remark || ''}
          onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
          placeholder="Add notes for this receiving..."
          className="bg-white border-2 border-primary/30 focus:border-primary"
          rows={2}
        />
      </div>

      <Button type="submit" className="w-full btn-primary">Create Receiving</Button>
    </form>
  );
}

function ReceivingEditForm({ receiving, warehouses, onSuccess }) {
  const [formData, setFormData] = useState({
    document: receiving.document || '',
    date: receiving.date ? receiving.date.split('T')[0] : new Date().toISOString().split('T')[0],
    location: receiving.location || '',
    remark: receiving.remark || '',
    items: receiving.items || []
  });

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === 'quantity' ? parseFloat(value) || 0 : value
    };
    // Recalculate amount if quantity changed
    if (field === 'quantity') {
      const price = newItems[index].price || (newItems[index].amount / (receiving.items[index]?.quantity || 1));
      newItems[index].amount = newItems[index].quantity * price;
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.put(`/receiving/${receiving.receiving_number}`, {
        ...formData,
        date: new Date(formData.date).toISOString()
      });
      toast.success('Receiving updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update receiving');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Document Reference</Label>
          <Input
            value={formData.document}
            onChange={(e) => setFormData({ ...formData, document: e.target.value })}
            required
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
        <div>
          <Label>Date</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
      </div>

      <div>
        <Label>Location (Warehouse)</Label>
        <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
          <SelectTrigger className="bg-white border-2 border-primary/30">
            <SelectValue placeholder="Select warehouse" />
          </SelectTrigger>
          <SelectContent>
            {warehouses.map((wh) => (
              <SelectItem key={wh.id} value={wh.id}>
                {wh.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border-t pt-4">
        <Label className="mb-2 block">Items</Label>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-2">PO</th>
                <th className="text-left p-2">Item</th>
                <th className="text-left p-2">Unit</th>
                <th className="text-center p-2">Quantity</th>
                <th className="text-right p-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item, idx) => (
                <tr key={idx} className="border-b">
                  <td className="p-2 text-sm">{item.po_number}</td>
                  <td className="p-2">{item.item_name}</td>
                  <td className="p-2">{item.unit}</td>
                  <td className="p-2">
                    <Input
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                      step="0.01"
                      min="0"
                      className="w-24 bg-white border-2 border-primary/30"
                    />
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(item.amount || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold bg-secondary/30">
                <td colSpan="4" className="p-2 text-right">Total:</td>
                <td className="p-2 text-right tabular-nums">
                  {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(
                    formData.items.reduce((sum, item) => sum + (item.amount || 0), 0)
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Remark */}
      <div>
        <Label>Remark (Optional)</Label>
        <Textarea
          value={formData.remark}
          onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
          placeholder="Add notes for this receiving..."
          className="bg-white border-2 border-primary/30 focus:border-primary"
          rows={2}
        />
      </div>

      <Button type="submit" className="w-full btn-primary">Update Receiving</Button>
    </form>
  );
}
